import React, { useEffect, useMemo, useState } from 'react';

import { Button, Modal } from '@/components';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import {
  SiSchedule,
  scheduledInstantAPI,
  type CreateSlotRiderInput,
} from '@/services/api/scheduled-instant';

import { ClientPicker, RiderPicker, type RiderSelection } from './pickers';

interface CreateScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a schedule is created or edited so the parent can refresh. */
  onCreated: () => void;
  /** When set, the modal edits this schedule instead of creating a new one. */
  editing?: SiSchedule | null;
}

const labelCls =
  'mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500';
const inputCls =
  'w-full rounded-gsm border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500';

// ISO weekday: 1 = Monday … 7 = Sunday (matches the BE DTO Min(1)/Max(7)).
const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Sen' },
  { value: 2, label: 'Sel' },
  { value: 3, label: 'Rab' },
  { value: 4, label: 'Kam' },
  { value: 5, label: 'Jum' },
  { value: 6, label: 'Sab' },
  { value: 7, label: 'Min' },
];

const CreateScheduleModal: React.FC<CreateScheduleModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  editing,
}) => {
  const isEdit = !!editing;
  const [providerId, setProviderId] = useState<number | ''>('');
  const [clientName, setClientName] = useState('');
  const [windowStartTime, setWindowStartTime] = useState('');
  const [windowEndTime, setWindowEndTime] = useState('');
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [batchLabel, setBatchLabel] = useState('');
  const [reason, setReason] = useState('');
  const [utama, setUtama] = useState<RiderSelection[]>([]);
  const [cadangan, setCadangan] = useState<RiderSelection[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Sync state each time the modal opens: prefill from `editing`, else blank.
  useEffect(() => {
    if (!isOpen) return;
    if (editing) {
      setProviderId(editing.providerId);
      setClientName(editing.clientName);
      setWindowStartTime(editing.windowStartTime);
      setWindowEndTime(editing.windowEndTime);
      setWeekdays([...editing.recurringWeekdays].sort((a, b) => a - b));
      setBatchLabel(editing.batchLabel ?? '');
    } else {
      setProviderId('');
      setClientName('');
      setWindowStartTime('');
      setWindowEndTime('');
      setWeekdays([]);
      setBatchLabel('');
    }
    setReason('');
    setUtama([]);
    setCadangan([]);
  }, [isOpen, editing]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const toggleDay = (v: number) =>
    setWeekdays((prev) =>
      prev.includes(v)
        ? prev.filter((d) => d !== v)
        : [...prev, v].sort((a, b) => a - b)
    );

  const validation = useMemo(() => {
    if (providerId === '' || !clientName) return 'Klien wajib dipilih.';
    if (!windowStartTime) return 'Jam mulai wajib diisi.';
    if (!windowEndTime) return 'Jam selesai wajib diisi.';
    if (windowEndTime <= windowStartTime)
      return 'Jam selesai harus setelah jam mulai.';
    if (weekdays.length === 0) return 'Pilih minimal satu hari berulang.';
    return null;
  }, [providerId, clientName, windowStartTime, windowEndTime, weekdays]);

  const handleSubmit = async () => {
    if (validation) {
      showErrorToast('Belum lengkap', validation);
      return;
    }
    if (providerId === '') return;
    setSubmitting(true);
    try {
      const payload = {
        providerId,
        clientName,
        windowStartTime,
        windowEndTime,
        recurringWeekdays: weekdays,
        batchLabel: batchLabel.trim() || null,
      };
      if (isEdit && editing) {
        const res = await scheduledInstantAPI.updateSchedule(editing.id, {
          ...payload,
          reason: reason.trim() || undefined,
        });
        const tail =
          res.updatedSlots || res.removedSlots
            ? ` · ${res.updatedSlots} slot diperbarui${
                res.removedSlots ? `, ${res.removedSlots} dihapus` : ''
              }`
            : '';
        showSuccessToast('Jadwal diperbarui', `${clientName}${tail}`);
      } else {
        const riders: CreateSlotRiderInput[] = [
          ...utama.map((r) => ({ ...r, role: 'utama' as const })),
          ...cadangan.map((r) => ({ ...r, role: 'cadangan' as const })),
        ];
        await scheduledInstantAPI.createSchedule({ ...payload, riders });
        showSuccessToast(
          'Jadwal dibuat',
          `${clientName} · ${windowStartTime}–${windowEndTime}`
        );
      }
      onCreated();
      onClose();
    } catch (e: any) {
      showErrorToast(
        isEdit ? 'Gagal menyimpan' : 'Gagal membuat jadwal',
        e.message || 'Terjadi kesalahan'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      icon={isEdit ? 'ri-edit-line' : 'ri-calendar-2-line'}
      iconTone="primary"
      title={isEdit ? 'Edit Jadwal' : 'Buat Jadwal Baru'}
      subtitle="Jadwal berulang (Master Jadwal) — men-generate slot harian otomatis"
      width="md"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="white" onClick={handleClose} disabled={submitting}>
            Batal
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !!validation}>
            {submitting
              ? 'Menyimpan…'
              : isEdit
                ? 'Simpan Perubahan'
                : 'Buat Jadwal'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <div>
          <label className={labelCls}>
            Klien <span className="text-red-500">*</span>
          </label>
          <ClientPicker
            open={isOpen}
            providerId={providerId}
            clientName={clientName}
            onChange={(sel) => {
              setProviderId(sel ? sel.providerId : '');
              setClientName(sel ? sel.clientName : '');
            }}
            disabled={submitting}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="sched-start" className={labelCls}>
              Jam Mulai <span className="text-red-500">*</span>
            </label>
            <input
              id="sched-start"
              type="time"
              value={windowStartTime}
              onChange={(e) => setWindowStartTime(e.target.value)}
              className={`${inputCls} font-mono`}
              disabled={submitting}
            />
          </div>
          <div>
            <label htmlFor="sched-end" className={labelCls}>
              Jam Selesai <span className="text-red-500">*</span>
            </label>
            <input
              id="sched-end"
              type="time"
              value={windowEndTime}
              onChange={(e) => setWindowEndTime(e.target.value)}
              className={`${inputCls} font-mono`}
              disabled={submitting}
            />
          </div>
        </div>

        <div>
          <label className={labelCls}>
            Hari Berulang <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAYS.map((d) => {
              const active = weekdays.includes(d.value);
              return (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => toggleDay(d.value)}
                  disabled={submitting}
                  className={`px-3 py-1.5 rounded-gsm text-xs font-semibold border transition-colors disabled:opacity-50 ${
                    active
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-primary hover:text-primary'
                  }`}
                  aria-pressed={active}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label htmlFor="sched-batch" className={labelCls}>
            Batch{' '}
            <span className="normal-case font-medium tracking-normal text-gray-400">
              (opsional)
            </span>
          </label>
          <input
            id="sched-batch"
            type="text"
            value={batchLabel}
            onChange={(e) => setBatchLabel(e.target.value)}
            placeholder="cth. Lunch"
            className={inputCls}
            disabled={submitting}
          />
        </div>

        {!isEdit && (
          <div className="space-y-3 rounded-gsm border border-gray-100 bg-gray-50/60 p-3">
            <p className="text-[11px] text-gray-500">
              Rider ini menjadi rider default jadwal — otomatis mengisi setiap
              slot harian yang di-generate. Bisa diubah nanti lewat Detail
              Jadwal.
            </p>
            <RiderPicker
              open={isOpen}
              title="Rider Utama"
              hint="opsional"
              riders={utama}
              onChange={setUtama}
              disabled={submitting}
            />
            <RiderPicker
              open={isOpen}
              title="Rider Cadangan"
              hint="opsional"
              riders={cadangan}
              onChange={setCadangan}
              disabled={submitting}
            />
          </div>
        )}

        {isEdit && (
          <>
            <div>
              <label htmlFor="sched-reason" className={labelCls}>
                Alasan Perubahan{' '}
                <span className="normal-case font-medium tracking-normal text-gray-400">
                  (opsional)
                </span>
              </label>
              <input
                id="sched-reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="cth. Jam pickup digeser klien"
                className={inputCls}
                disabled={submitting}
              />
            </div>
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-gsm px-3 py-2">
              Perubahan ini juga memperbarui slot mendatang yang sudah
              ter-generate (hari &amp; jam). Slot yang harinya dihapus &amp;
              belum ada rider akan dihapus; slot hari ini &amp; lampau tidak
              tersentuh.
            </p>
          </>
        )}

        {validation && (
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-gsm px-3 py-2">
            {validation}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default CreateScheduleModal;
